import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { STATE_LABELS, dimensionLabel, display, tagLabel } from '../lib/score.js'

/**
 * The append-only history, finally drawn (1B-2).
 *
 * Collapsed by default behind one control — the current score stays visually
 * primary, and most visits never open this. The rows come newest-first from
 * the server a page at a time; "Show earlier" appends the next page rather
 * than replacing, so a long history reads as one list however it arrived.
 *
 * Nothing here invites a re-rate. The timeline is a record of what already
 * happened, not a prompt — that is the parent plan's identity guard, and it
 * is why there is no button on any row.
 */
export default function RevisionTimeline({ kind, id, revisionCount }) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(
    async (beforeId) => {
      setBusy(true)
      setError('')
      try {
        const fetchPage = kind === 'song' ? api.songRatingRevisions : api.albumRatingRevisions
        const body = await fetchPage(id, { beforeId })
        setPage((current) =>
          beforeId && current
            ? {
                asOf: revisionCount,
                items: [...current.items, ...body.items],
                next_before_id: body.next_before_id,
              }
            : { asOf: revisionCount, ...body },
        )
      } catch {
        setError('Sonologue could not load this history just now.')
      } finally {
        setBusy(false)
      }
    },
    [kind, id, revisionCount],
  )

  // A save on the page above bumps `revisionCount`, and rows fetched before
  // it are stale the moment that happens. An open timeline reloads from the
  // top; a folded one spends nothing until its next expand.
  useEffect(() => {
    if (open && page !== null && page.asOf !== revisionCount) load()
  }, [open, page, revisionCount, load])

  function toggle() {
    const next = !open
    setOpen(next)
    if (next && page === null) load()
  }

  return (
    <div className="timeline">
      <button type="button" className="link-button" aria-expanded={open} onClick={toggle}>
        Revision history
      </button>

      {!open ? null : error ? (
        <p className="footnote" role="alert">
          {error}
        </p>
      ) : page === null ? (
        <p className="footnote">Loading…</p>
      ) : (
        <>
          <ol className="timeline-list">
            {page.items.map((row) => (
              <li key={row.id} className="timeline-row">
                <span className="timeline-score num">{display(row.score)}</span>
                <span className="row-main">
                  <span className="timeline-meta footnote">
                    {new Date(row.created_at).toLocaleDateString()}
                    {row.track_average_at_time != null
                      ? ` · tracks averaged ${display(row.track_average_at_time)} then`
                      : ''}
                  </span>
                  {row.note ? <span className="timeline-note">{row.note}</span> : null}
                  {/*
                    Only the dimensions this revision actually held. An empty
                    object renders nothing rather than eight dashes — a rating
                    with no sub-scores is the common case and should not grow a
                    row of absences.
                  */}
                  {Object.keys(row.subscores || {}).length ? (
                    <span className="timeline-subscores">
                      {Object.entries(row.subscores).map(([slug, value]) => (
                        <span key={slug}>
                          {dimensionLabel(slug)} <span className="num">{display(value)}</span>
                        </span>
                      ))}
                    </span>
                  ) : null}
                  {/*
                    The tags that revision carried. Spelled from the slug
                    rather than looked up: a revision may hold a tag the
                    vocabulary has since retired, and resolving it against
                    today's list would render a hole where the answer is
                    "whatever it was called then" — see `tagLabel`.
                  */}
                  {row.tags?.length ? (
                    <span className="timeline-tags">
                      {row.tags.map((slug) => (
                        <span key={slug}>{tagLabel(slug)}</span>
                      ))}
                    </span>
                  ) : null}
                </span>
                {row.state ? (
                  <span className="state-chip">{STATE_LABELS[row.state]}</span>
                ) : null}
              </li>
            ))}
          </ol>
          {page.next_before_id ? (
            <button
              type="button"
              className="link-button"
              disabled={busy}
              onClick={() => load(page.next_before_id)}
            >
              Show earlier
            </button>
          ) : null}
        </>
      )}
    </div>
  )
}
